/**
 * Settings → Developer, for Mauvely developers alone: developer mode, API
 * keys, and what this account used over 30 or 90 days, by day and by kind.
 * Costs are model cost in US dollars, as the Usage tab's units count it.
 */

import { useEffect, useMemo, useState } from 'react';
import { getUsageHistory, patchSettings, type UsageHistory } from '../../api';
import { ds } from '../../ds';
import { useApp } from '../../state';
import { MonoLabel } from '../ui';
import { ApiKeys } from './ApiKeys';
import { dollars } from './format';
import { usageModelName } from './UsageTab';

/** The chart's kinds: the usage ledger's purposes, grouped. */
const KINDS = [
  { key: 'chat', label: 'Chat', colour: 'var(--violet-500)', purposes: ['chat', 'onboarding'] },
  { key: 'side', label: 'Titles, suggestions and memory', colour: 'var(--indigo-400)', purposes: ['title', 'suggestion', 'memory'] },
  { key: 'image', label: 'Images', colour: 'var(--teal-500)', purposes: ['image'] },
  { key: 'search', label: 'Web search', colour: 'var(--indigo-200)', purposes: ['search'] },
  { key: 'voice', label: 'Voice and dictation', colour: 'var(--mauve-400)', purposes: ['speech', 'transcribe'] },
  { key: 'computer', label: 'Computer', colour: 'var(--warning-500)', purposes: ['sandbox'] }
] as const;

const OTHER = { key: 'other', label: 'Other', colour: 'var(--slate-400)' } as const;

const kindOf = (purpose: string) => KINDS.find((k) => (k.purposes as readonly string[]).includes(purpose)) ?? OTHER;

const card: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderRadius: 'var(--radius-app-card)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12
};

export function DeveloperTab() {
  const { me, patchMe, refreshMe } = useApp();
  const { Switch } = ds();
  const [days, setDays] = useState<30 | 90>(30);
  const [history, setHistory] = useState<UsageHistory | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    getUsageHistory(days)
      .then((h) => {
        if (!cancelled) setHistory(h);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (!me) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card, flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 14, color: 'var(--text-strong)' }}>Developer mode</span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Assist knows you are a Mauvely developer, and shows its instructions when you ask.
          </span>
        </span>
        <Switch
          checked={me.settings.developerMode}
          onChange={(v) => {
            patchSettings({ developerMode: v })
              .then((settings) => patchMe((x) => ({ ...x, settings })))
              .catch(() => void refreshMe());
          }}
        />
      </div>

      <ApiKeys />

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MonoLabel style={{ flex: 1 }}>Last {days} days</MonoLabel>
          {([30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              className="bare hov-tint"
              aria-pressed={d === days}
              onClick={() => setDays(d)}
              style={{
                height: 26,
                padding: '0 10px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                background: d === days ? 'var(--primary-soft)' : 'transparent',
                color: d === days ? 'var(--primary-text)' : 'var(--text-muted)'
              }}
            >
              {d} days
            </button>
          ))}
        </div>
        {failed ? (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Usage did not load.</span>
        ) : history ? (
          <UsageChart history={history} />
        ) : (
          <div style={{ height: 160 }} />
        )}
      </div>

      {history ? <Tables history={history} /> : null}

      <a href="https://internal.mauvely.com" target="_blank" rel="noopener" style={{ fontSize: 13.5, alignSelf: 'flex-start' }}>
        Open the internal console
      </a>
    </div>
  );
}

/** Each day's cost, stacked by kind, oldest on the left. */
function UsageChart({ history }: { history: UsageHistory }) {
  const { bars, max } = useMemo(() => {
    const today = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
    const days = Array.from({ length: history.days }, (_, i) => new Date(today - (history.days - 1 - i) * 86_400_000).toISOString().slice(0, 10));
    const byDay = new Map(days.map((d) => [d, new Map<string, number>()]));
    for (const row of history.byDay) {
      const day = byDay.get(row.day);
      if (!day) continue;
      const kind = kindOf(row.purpose).key;
      day.set(kind, (day.get(kind) ?? 0) + row.units);
    }
    const bars = days.map((d) => ({ day: d, kinds: byDay.get(d)! }));
    const max = Math.max(1, ...bars.map((b) => [...b.kinds.values()].reduce((n, u) => n + u, 0)));
    return { bars, max };
  }, [history]);

  const W = 720;
  const H = 150;
  const step = W / bars.length;
  const gap = Math.min(3, step * 0.25);
  const used = [...KINDS, OTHER].filter((k) => bars.some((b) => b.kinds.has(k.key)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-faint)' }}>
        <span>{dollars(max)} a day at most</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Cost by day">
        <line x1={0} x2={W} y1={H - 0.5} y2={H - 0.5} stroke="var(--border-subtle)" />
        {bars.map((b, i) => {
          let y = H;
          return (
            <g key={b.day}>
              {[...KINDS, OTHER].map((k) => {
                const units = b.kinds.get(k.key);
                if (!units) return null;
                const h = Math.max(1, (units / max) * (H - 4));
                y -= h;
                return (
                  <rect key={k.key} x={i * step + gap / 2} y={y} width={step - gap} height={h} fill={k.colour} rx={1}>
                    <title>{`${b.day} · ${k.label}: ${dollars(units)}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-faint)' }}>
        <span>{bars[0]?.day}</span>
        <span>{bars[bars.length - 1]?.day}</span>
      </div>
      {used.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
          {used.map((k) => (
            <span key={k.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)' }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: k.colour }} />
              {k.label}
            </span>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nothing on Mauvely’s account in this time.</span>
      )}
    </div>
  );
}

function Tables({ history }: { history: UsageHistory }) {
  const { catalogue } = useApp();
  const nameOf = (id: string) => usageModelName(id, catalogue?.models);
  const head: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, textAlign: 'right', padding: '8px 12px' };
  const cell: React.CSSProperties = { fontSize: 13, color: 'var(--text-body)', textAlign: 'right', padding: '8px 12px', fontVariantNumeric: 'tabular-nums' };
  const first: React.CSSProperties = { ...cell, textAlign: 'left', color: 'var(--text-strong)' };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <MonoLabel>By model</MonoLabel>
        <div className="scroll" style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflowX: 'auto' }}>
          <table className="selectable" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>Model</th>
                <th style={head}>Paid by</th>
                <th style={head}>Calls</th>
                <th style={head}>In</th>
                <th style={head}>Out</th>
                <th style={head}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {history.byModel.map((r) => (
                <tr key={`${r.model}:${r.source}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={first}>{nameOf(r.model)}</td>
                  <td style={cell}>{r.source === 'mauvely' ? 'Mauvely' : 'Own key'}</td>
                  <td style={cell}>{r.calls.toLocaleString('en-GB')}</td>
                  <td style={cell}>{r.tokensIn.toLocaleString('en-GB')}</td>
                  <td style={cell}>{r.tokensOut.toLocaleString('en-GB')}</td>
                  <td style={cell}>{r.source === 'mauvely' ? dollars(r.units) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <MonoLabel>By kind</MonoLabel>
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
          <table className="selectable" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {history.byPurpose.map((r) => (
                <tr key={r.purpose} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={first}>{r.purpose}</td>
                  <td style={cell}>{r.calls.toLocaleString('en-GB')} calls</td>
                  <td style={cell}>{dollars(r.units)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
